/**
 * useUrbanEntitiesLayer — Capa MapLibre para urban_entities
 *
 * @remarks
 * Renderiza urban_entities como puntos en el mapa, coloreados por entityType.
 * Patrón idéntico a useBuildingMapLayer: source GeoJSON + layers circle.
 * Soporta toggle de visibilidad sin destruir el source.
 *
 * @module urban-inventory/hooks
 */

import type { MapMouseEvent, MapGeoJSONFeature } from 'maplibre-gl';
import { useCallback, useEffect, useRef } from 'react';
import { mapEngine } from '../../../map-engine/MapEngineService';
import { useUrbanInventoryStore } from '../store/urban-inventory-store';
import type { UrbanEntity } from '../types/urban-entity.types';
import { ENTITY_TYPE_COLORS } from '../utils/urban-entity-colors';

// ─── IDs de layers ────────────────────────────────────────────────────────────

const SOURCE_ID = 'urban-entities-source';
const LAYER_POINTS = 'urban-entities-points';
const LAYER_SELECTED = 'urban-entities-selected';

function buildColorExpression(): unknown[] {
  const matchArgs: unknown[] = ['match', ['get', 'entityType']];
  for (const [type, color] of Object.entries(ENTITY_TYPE_COLORS)) {
    if (type === 'default') continue;
    matchArgs.push(type, color);
  }
  matchArgs.push(ENTITY_TYPE_COLORS.default);
  return matchArgs;
}

// ─── GeoJSON builder ──────────────────────────────────────────────────────────

function entitiesToGeoJSON(entities: UrbanEntity[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: entities.map(e => ({
      type: 'Feature',
      id: e.id,
      geometry: e.geometry,
      properties: {
        id: e.id,
        entityType: e.entityType,
        originalEntityType: e.originalEntityType ?? null,
        status: e.status,
        importBatchId: e.importBatchId ?? null,
      },
    })),
  };
}

// ─── Layer setup / cleanup (extracted for max-lines-per-function) ─────────────

function addEntityLayers(
  entities: UrbanEntity[],
  colorExpr: unknown[],
  visibleRef: React.RefObject<boolean | null>,
  layersReady: React.MutableRefObject<boolean>
): void {
  const map = mapEngine.getMap();
  if (!map) return;

  if (!map.getSource(SOURCE_ID)) {
    map.addSource(SOURCE_ID, { type: 'geojson', data: entitiesToGeoJSON(entities) });
  }

  if (!map.getLayer(LAYER_POINTS)) {
    map.addLayer({
      id: LAYER_POINTS,
      type: 'circle',
      source: SOURCE_ID,
      paint: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        'circle-color': colorExpr as any,
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 12, 4, 15, 7, 18, 10],
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 1.5,
        'circle-opacity': [
          'case',
          ['==', ['get', 'status'], 'removed'],
          0.3,
          ['==', ['get', 'status'], 'inactive'],
          0.5,
          0.9,
        ],
      },
    });
  }

  if (!map.getLayer(LAYER_SELECTED)) {
    map.addLayer({
      id: LAYER_SELECTED,
      type: 'circle',
      source: SOURCE_ID,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filter: ['==', ['get', 'id'], ''] as any,
      paint: {
        'circle-color': 'transparent',
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 12, 10, 15, 16, 18, 22],
        'circle-opacity': 0,
        'circle-stroke-color': '#38bdf8',
        'circle-stroke-width': 3,
        'circle-stroke-opacity': 1,
      },
    });
  }

  const vis = visibleRef.current ? 'visible' : 'none';
  if (map.getLayer(LAYER_POINTS)) map.setLayoutProperty(LAYER_POINTS, 'visibility', vis);
  if (map.getLayer(LAYER_SELECTED)) map.setLayoutProperty(LAYER_SELECTED, 'visibility', vis);
  layersReady.current = true;
}

function removeEntityLayers(layersReady: React.MutableRefObject<boolean>): void {
  const m = mapEngine.getMap();
  if (!m) {
    layersReady.current = false;
    return;
  }
  try {
    if (m.getStyle()) {
      if (m.getLayer(LAYER_SELECTED)) m.removeLayer(LAYER_SELECTED);
      if (m.getLayer(LAYER_POINTS)) m.removeLayer(LAYER_POINTS);
      if (m.getSource(SOURCE_ID)) m.removeSource(SOURCE_ID);
    }
  } catch {
    // Map style may be destroyed during unmount — safe to ignore
  }
  layersReady.current = false;
}

// ─── Event handler factories ──────────────────────────────────────────────────

function createClickHandler(
  setHoveredEntity: (id: string | null) => void
): (e: MapMouseEvent & { features?: MapGeoJSONFeature[] }) => void {
  return e => {
    if (!e.features?.length) return;
    const props = (e.features[0].properties ?? {}) as Record<string, unknown>;
    const id = String(props['id'] ?? '');
    if (!id) return;
    setHoveredEntity(null);
    window.dispatchEvent(new CustomEvent('urban-entity-clicked', { detail: { entityId: id } }));
  };
}

function createStyleDataHandler(
  setup: () => void,
  visibleRef: React.RefObject<boolean | null>,
  lastStyleDataTime: React.MutableRefObject<number>,
  layersReady: React.MutableRefObject<boolean>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): (e: any) => void {
  return e => {
    if (e.dataType !== 'style' || !visibleRef.current) return;
    const now = Date.now();
    if (now - lastStyleDataTime.current < 1000) return;
    lastStyleDataTime.current = now;
    const map = mapEngine.getMap();
    if (map?.getSource(SOURCE_ID)) return;
    layersReady.current = false;
    window.setTimeout(() => {
      setup();
    }, 150);
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export interface UseUrbanEntitiesLayerOptions {
  entities: UrbanEntity[];
  visible: boolean;
}

export function useUrbanEntitiesLayer({ entities, visible }: UseUrbanEntitiesLayerOptions): void {
  const layersReady = useRef(false);
  const visibleRef = useRef(visible);
  const lastStyleDataTime = useRef(0);
  const setHoveredEntity = useUrbanInventoryStore(s => s.setHoveredEntity);
  const hoveredEntityId = useUrbanInventoryStore(s => s.hoveredEntityId);
  const selectedEntityId = useUrbanInventoryStore(s => s.selectedEntityId);

  useEffect(() => {
    visibleRef.current = visible;
  }, [visible]);

  useEffect(() => {
    if (!layersReady.current) return;
    const map = mapEngine.getMap();
    if (!map) return;
    const source = map.getSource(SOURCE_ID);
    if (source) source.setData(entitiesToGeoJSON(entities));
  }, [entities]);

  useEffect(() => {
    if (!layersReady.current) return;
    const map = mapEngine.getMap();
    if (!map) return;
    if (map.getLayer(LAYER_SELECTED)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      map.setFilter(LAYER_SELECTED, ['==', ['get', 'id'], selectedEntityId ?? ''] as any);
    }
  }, [selectedEntityId]);

  useEffect(() => {
    const map = mapEngine.getMap();
    if (map) map.getCanvas().style.cursor = hoveredEntityId ? 'pointer' : '';
  }, [hoveredEntityId]);

  const colorExpr = buildColorExpression();
  const setup = useCallback(
    () => addEntityLayers(entities, colorExpr, visibleRef, layersReady),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    const map = mapEngine.getMap();
    if (!map) return;
    const handleClick = createClickHandler(setHoveredEntity);
    const handleMouseEnter = (e: MapMouseEvent & { features?: MapGeoJSONFeature[] }): void => {
      const id = String((e.features?.[0]?.properties ?? {})['id'] ?? '');
      if (id) setHoveredEntity(id);
    };
    const handleMouseLeave = (): void => {
      setHoveredEntity(null);
    };
    const handleStyleData = createStyleDataHandler(
      setup,
      visibleRef,
      lastStyleDataTime,
      layersReady
    );

    map.on('click', LAYER_POINTS, handleClick);
    map.on('mouseenter', LAYER_POINTS, handleMouseEnter);
    map.on('mouseleave', LAYER_POINTS, handleMouseLeave);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    map.on('styledata', handleStyleData as any);

    if (map.isStyleLoaded()) {
      setup();
    } else {
      void map.once('load', setup);
    }

    return (): void => {
      const m = mapEngine.getMap();
      if (m) {
        m.off('click', LAYER_POINTS, handleClick);
        m.off('mouseenter', LAYER_POINTS, handleMouseEnter);
        m.off('mouseleave', LAYER_POINTS, handleMouseLeave);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        m.off('styledata', handleStyleData as any);
      }
      removeEntityLayers(layersReady);
    };
  }, [setup, setHoveredEntity]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!layersReady.current) return;
    const map = mapEngine.getMap();
    if (!map) return;
    const vis = visible ? 'visible' : 'none';
    if (map.getLayer(LAYER_POINTS)) map.setLayoutProperty(LAYER_POINTS, 'visibility', vis);
    if (map.getLayer(LAYER_SELECTED)) map.setLayoutProperty(LAYER_SELECTED, 'visibility', vis);
  }, [visible]);
}

// Re-export para compatibilidad con consumidores existentes
export { colorForType } from '../utils/urban-entity-colors';
